import React, { type ComponentType, useState, type Ref, useEffect, Children } from 'react';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import IconInformationCircle from '@/assets/icons/icon-information-circle.svg?react';
import { InfoTooltip } from '@/components/tooltips/InfoTooltip/InfoTooltip';

interface SelectFieldProps {
  className?: string;
  classNameContent?: string;
  classNameValue?: string;
  classNameTrigger?: string;
  placeholder?: string;
  text?: string;
  showInfoIcon?: boolean;
  infoTooltipText?: string;
  size?: 'small' | 'medium' | 'large' | 'full' | 'none';
  children: React.ReactNode;
  icon?: ComponentType<React.SVGProps<SVGSVGElement>> | null;
  defaultValue?: string;
  customValues?: boolean;
  onValueChange?: (value: string) => void;
  triggerRef?: Ref<HTMLButtonElement>;
  triggerButtonRef?: Ref<HTMLButtonElement>;
  childrenTrigger?: React.ReactNode; // Custom trigger content (e.g., only icon)
  // Optional positioning overrides
  side?: 'top' | 'bottom';
  avoidCollisions?: boolean;
  truncate?: boolean;
  dataTestId?: string;
  'data-qa-selector'?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  className = '',
  classNameContent = '',
  classNameValue = '',
  classNameTrigger = '',
  text = 'Text',
  showInfoIcon = false,
  infoTooltipText,
  size = 'small',
  children = [
    <SelectItem value="light" key="light">
      Light
    </SelectItem>,
    <SelectItem value="dark" key="dark">
      Dark
    </SelectItem>,
    <SelectItem value="system" key="system">
      System
    </SelectItem>,
  ],
  placeholder = '',
  icon: Icon,
  defaultValue,
  onValueChange,
  triggerRef,
  triggerButtonRef,
  childrenTrigger,
  side,
  avoidCollisions,
  truncate = true,
  dataTestId,
  'data-qa-selector': dataQaSelector,
}) => {
  const [value, setValue] = useState(defaultValue || '');

  const handleValueChange = (newValue: string) => {
    setValue(newValue);
    onValueChange?.(newValue);
  };
  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      // onValueChange?.(defaultValue);
    }
  }, [defaultValue]);

  // Check if children is empty or null
  const hasChildren = Children.count(children) > 0;

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {text && (
        <p className="text-button-sm mr-2 font-medium text-neutral-500">
          <span className="flex items-center gap-1">
            <span>{text}</span>
            {showInfoIcon &&
              (infoTooltipText ? (
                <InfoTooltip text={infoTooltipText} />
              ) : (
                <IconInformationCircle className="h-4 w-4 text-neutral-300" />
              ))}
          </span>
        </p>
      )}

      <Select defaultValue={defaultValue} value={value} onValueChange={handleValueChange}>
        <SelectTrigger
          ref={triggerButtonRef}
          hideIcon={!!childrenTrigger}
          {...(dataTestId && { 'data-testid': dataTestId })}
          data-qa-selector={dataQaSelector}
          className={
            childrenTrigger
              ? clsx('shadow-none', classNameTrigger)
              : clsx(
                  'relative w-full shadow-none',
                  truncate && 'overflow-hidden',
                  {
                    'max-w-[124px]': size === 'small',
                    'max-w-[140px]': size === 'medium',
                    'max-w-[160px]': size === 'large',
                    'max-w-[216px]': size === 'full',
                  },
                  classNameTrigger,
                )
          }
        >
          {childrenTrigger ? (
            <>{childrenTrigger}</>
          ) : (
            <>
              {Icon && <Icon className="h-4.5 w-4.5 text-neutral-500" />}
              <SelectValue
                className={classNameValue}
                ref={triggerRef}
                placeholder={placeholder}
                truncate={truncate}
              />
            </>
          )}
        </SelectTrigger>
        <SelectContent
          side={side}
          avoidCollisions={avoidCollisions}
          className={clsx(classNameContent)}
          truncate={truncate}
        >
          {hasChildren ? (
            children
          ) : (
            <SelectItem value="__no_options__" disabled>
              No options available
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
